import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.js';
import { ApiClient } from '../lib/api.js';

type Tab = 'pending' | 'health' | 'users' | 'all';

export function AdminDashboard() {
  const { getToken } = useAuth();
  const api = new ApiClient(getToken);
  const [params, setParams] = useSearchParams();
  const tab: Tab = (params.get('tab') as Tab) || 'pending';
  const [me, setMe] = useState<any>(null);

  useEffect(() => { api.me().then(setMe).catch(console.error); /* eslint-disable-next-line */ }, []);
  if (me && !me.isAdmin) return <Navigate to="/app/home" replace />;

  return (
    <div className="page">
      <div className="page-head">
        <h1>Admin</h1>
        <p className="lede">Live system status and user activity. Visible only to admins.</p>
      </div>

      <div className="tabs">
        <button className={tab === 'pending' ? 'tab active' : 'tab'} onClick={() => setParams({ tab: 'pending' })}>Pending approvals</button>
        <button className={tab === 'all' ? 'tab active' : 'tab'} onClick={() => setParams({ tab: 'all' })}>All users</button>
        <button className={tab === 'health' ? 'tab active' : 'tab'} onClick={() => setParams({ tab: 'health' })}>System health</button>
        <button className={tab === 'users' ? 'tab active' : 'tab'} onClick={() => setParams({ tab: 'users' })}>User stats</button>
      </div>

      <div className="row" style={{ margin: '12px 0' }}>
        <Link to="/app/admin/ranking" className="muted small">Ranking playbook editor →</Link>
      </div>

      {tab === 'pending' && <PendingTab api={api} meUid={me?.uid} />}
      {tab === 'all' && <AllUsersTab api={api} meUid={me?.uid} />}
      {tab === 'health' && <HealthTab api={api} />}
      {tab === 'users' && <UsersTab api={api} />}
    </div>
  );
}

// ----------------------------------------------------------
// Health tab
// ----------------------------------------------------------
function HealthTab({ api }: { api: ApiClient }) {
  const [report, setReport] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  async function load() {
    setRefreshing(true); setErr(null);
    try { setReport(await api.adminHealth()); } catch (e: any) { setErr(e.message); }
    setRefreshing(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (err) return <div className="error">{err}</div>;
  if (!report) return <div className="loading">Checking services…</div>;

  return (
    <>
      <div className="card">
        <div className="button-row between">
          <div>
            <div className="label">Overall</div>
            <div className="muted small">{report.services.length} services · region {report.region} · checked {new Date(report.generatedAt).toLocaleTimeString()}</div>
          </div>
          <button onClick={load} disabled={refreshing}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <div className="stack">
        {report.services.map((s: any) => (
          <div className="service-row" key={s.name}>
            <span className={`status-dot status-${s.status}`} />
            <div className="service-info">
              <strong>{s.name}</strong>
              <div className="muted small">{s.detail ?? '—'}</div>
            </div>
            <div className="service-meta">
              <span className="muted small">{statusLabel(s.status)}</span>
              {s.latencyMs != null && <div className="muted small">{s.latencyMs}ms</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="card subtle" style={{ marginTop: 16 }}>
        <h4>External links</h4>
        <div className="button-row">
          <a href="https://console.cloud.google.com/run/detail/us-central1/tweetvia-api/metrics?project=tweetvia" target="_blank" rel="noreferrer">Cloud Run metrics</a>
          <a href="https://console.cloud.google.com/cloudscheduler?project=tweetvia" target="_blank" rel="noreferrer">Cloud Scheduler</a>
          <a href="https://console.firebase.google.com/project/tweetvia/firestore" target="_blank" rel="noreferrer">Firestore</a>
          <a href="https://console.cloud.google.com/billing/budgets" target="_blank" rel="noreferrer">Budget</a>
        </div>
      </div>
    </>
  );
}

function statusLabel(s: string): string {
  if (s === 'ok') return 'Healthy';
  if (s === 'degraded') return 'Degraded';
  if (s === 'down') return 'Down';
  if (s === 'not_configured') return 'Not configured';
  return s;
}

// ----------------------------------------------------------
// Users tab
// ----------------------------------------------------------
function UsersTab({ api }: { api: ApiClient }) {
  const [stats, setStats] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  async function load() {
    setRefreshing(true); setErr(null);
    try { setStats(await api.adminStats()); } catch (e: any) { setErr(e.message); }
    setRefreshing(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (err) return <div className="error">{err}</div>;
  if (!stats) return <div className="loading">Crunching numbers…</div>;

  return (
    <>
      <div className="card">
        <div className="button-row between">
          <div>
            <div className="label">User totals</div>
            <div className="muted small">Sampled from last 500 generations per user · pricing assumes gemini-flash-latest</div>
          </div>
          <button onClick={load} disabled={refreshing}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <div className="stat-strip">
        <StatCard label="Total users" value={stats.totalUsers} />
        <StatCard label="Paid users" value={stats.paidUsers} sub={`${stats.paidConversionPct}% conversion`} />
        <StatCard label="Generations / mo" value={stats.totalGenerationsThisMonth} />
        <StatCard label="Estimated LLM cost" value={`$${stats.llm.estimatedCostUsd.toFixed(2)}`} sub={`${formatTokens(stats.llm.totalInputTokens)} in · ${formatTokens(stats.llm.totalOutputTokens)} out`} />
      </div>

      <div className="card">
        <h4>By plan</h4>
        <div className="chip-row">
          <span className="chip">free · {stats.byPlan.free}</span>
          <span className="chip">creator · {stats.byPlan.creator}</span>
          <span className="chip">pro · {stats.byPlan.pro}</span>
          <span className="chip">team · {stats.byPlan.team}</span>
        </div>
      </div>

      <div className="card">
        <h4>Activity</h4>
        <div className="kv">
          <div className="muted small">Active monitors</div><div>{stats.totalMonitors}</div>
          <div className="muted small">Curated posts</div><div>{stats.totalCuratedPosts}</div>
          <div className="muted small">LLM tokens in</div><div>{formatTokens(stats.llm.totalInputTokens)} <span className="muted small">@ ${stats.llm.inputCostPerMUsd.toFixed(2)} / M</span></div>
          <div className="muted small">LLM tokens out</div><div>{formatTokens(stats.llm.totalOutputTokens)} <span className="muted small">@ ${stats.llm.outputCostPerMUsd.toFixed(2)} / M</span></div>
        </div>
      </div>

      <div className="card">
        <h4>Top users by activity</h4>
        {stats.topUsers.length === 0 ? (
          <p className="muted small">No user activity yet.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>User</th><th>Plan</th><th style={{ textAlign: 'right' }}>Generations</th><th style={{ textAlign: 'right' }}>Est. cost</th></tr>
            </thead>
            <tbody>
              {stats.topUsers.map((u: any) => (
                <tr key={u.uid}>
                  <td>{u.email ?? u.uid}</td>
                  <td><span className="chip">{u.plan}</span></td>
                  <td style={{ textAlign: 'right' }}>{u.generations}</td>
                  <td style={{ textAlign: 'right' }}>${u.estimatedCostUsd.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function StatCard({ label, value, sub }: { label: string; value: any; sub?: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="muted small">{sub}</div>}
    </div>
  );
}

function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(1) + 'k';
  return (n / 1_000_000).toFixed(2) + 'M';
}

// ----------------------------------------------------------
// Pending approvals tab
// ----------------------------------------------------------
function PendingTab({ api, meUid }: { api: ApiClient; meUid?: string }) {
  const [users, setUsers] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);

  async function load() {
    setErr(null);
    try { setUsers((await api.adminPendingUsers()).users ?? []); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function setStatus(uid: string, status: 'approved' | 'rejected') {
    setBusy(uid); setErr(null);
    try { await api.adminSetUserAccess(uid, status); await load(); }
    catch (e: any) { setErr(e.message); }
    setBusy(null);
  }

  async function doDelete(uid: string) {
    setBusy(uid); setErr(null);
    try { await api.adminDeleteUser(uid); setConfirmDelete(null); await load(); }
    catch (e: any) { setErr(e.message); }
    setBusy(null);
  }

  return (
    <>
      <div className="card">
        <div className="button-row between">
          <div>
            <div className="label">Pending approvals</div>
            <div className="muted small">{users.length} user{users.length === 1 ? '' : 's'} waiting</div>
          </div>
          <button onClick={load}>Refresh</button>
        </div>
        {err && <div className="error">{err}</div>}
      </div>

      {users.length === 0 ? (
        <div className="card empty">
          <p className="muted">Nobody's waiting. Good.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr><th>User</th><th>Signed up</th><th style={{ textAlign: 'right' }}>Action</th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.uid}>
                <td>
                  <div><strong>{u.email ?? u.uid}</strong></div>
                  {u.displayName && <div className="muted small">{u.displayName}</div>}
                </td>
                <td className="muted small">{formatCreatedAt(u.createdAt)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="ghost danger" onClick={() => setConfirmDelete(u)} disabled={busy === u.uid || u.uid === meUid}>Delete</button>
                  <button className="ghost" onClick={() => setStatus(u.uid, 'rejected')} disabled={busy === u.uid} style={{ marginLeft: 6 }}>Reject</button>
                  <button className="primary" onClick={() => setStatus(u.uid, 'approved')} disabled={busy === u.uid} style={{ marginLeft: 6 }}>
                    {busy === u.uid ? '…' : 'Approve'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDelete && (
        <DeleteUserModal user={confirmDelete} busy={busy === confirmDelete.uid} onCancel={() => setConfirmDelete(null)} onConfirm={() => doDelete(confirmDelete.uid)} />
      )}
    </>
  );
}

// ----------------------------------------------------------
// All users tab — list every user with filter + delete
// ----------------------------------------------------------
function AllUsersTab({ api, meUid }: { api: ApiClient; meUid?: string }) {
  const [users, setUsers] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'approved' | 'pending' | 'rejected'>('all');
  const [q, setQ] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null);

  async function load() {
    setErr(null);
    try {
      const res = await api.adminListUsers(filter === 'all' ? undefined : filter);
      setUsers(res.users ?? []);
    } catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function doDelete(uid: string) {
    setBusy(uid); setErr(null);
    try { await api.adminDeleteUser(uid); setConfirmDelete(null); await load(); }
    catch (e: any) { setErr(e.message); }
    setBusy(null);
  }

  const filtered = users.filter(u => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return String(u.email ?? '').toLowerCase().includes(needle)
      || String(u.displayName ?? '').toLowerCase().includes(needle)
      || String(u.uid ?? '').toLowerCase().includes(needle);
  });

  return (
    <>
      <div className="card">
        <div className="button-row between">
          <div>
            <div className="label">All users</div>
            <div className="muted small">{filtered.length} shown · {users.length} total</div>
          </div>
          <button onClick={load}>Refresh</button>
        </div>
        <div className="button-row" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
          <select value={filter} onChange={e => setFilter(e.target.value as any)}>
            <option value="all">All statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
          <input
            placeholder="Search email, name, uid…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
        </div>
        {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      </div>

      {filtered.length === 0 ? (
        <div className="card empty"><p className="muted">No users match.</p></div>
      ) : (
        <table className="data-table">
          <thead>
            <tr><th>User</th><th>Status</th><th>Plan</th><th>Signed up</th><th style={{ textAlign: 'right' }}>Action</th></tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.uid}>
                <td>
                  <div><strong>{u.email ?? u.uid}</strong></div>
                  {u.displayName && <div className="muted small">{u.displayName}</div>}
                </td>
                <td><span className="chip">{u.accessStatus ?? '—'}</span></td>
                <td><span className="chip">{u.plan ?? 'free'}</span></td>
                <td className="muted small">{formatCreatedAt(u.createdAt)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="ghost danger"
                    onClick={() => setConfirmDelete(u)}
                    disabled={busy === u.uid || u.uid === meUid}
                    title={u.uid === meUid ? "Can't delete yourself" : 'Hard-delete user + all data'}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDelete && (
        <DeleteUserModal user={confirmDelete} busy={busy === confirmDelete.uid} onCancel={() => setConfirmDelete(null)} onConfirm={() => doDelete(confirmDelete.uid)} />
      )}
    </>
  );
}

function DeleteUserModal({ user, busy, onCancel, onConfirm }: { user: any; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  const [typed, setTyped] = useState('');
  const expected = String(user.email ?? user.uid);
  const armed = typed.trim() === expected;
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Delete this user?</h3>
        <p className="muted small">
          This will permanently remove <strong>{expected}</strong> and all of their data: tweets imported for voice, voice profile,
          generation history, keyword monitors, curated posts, drafts, and their Firebase sign-in identity. <strong>Cannot be undone.</strong>
        </p>
        <p className="muted small">Type <code>{expected}</code> to confirm:</p>
        <input value={typed} onChange={e => setTyped(e.target.value)} placeholder={expected} style={{ width: '100%', marginBottom: 12 }} autoFocus />
        <div className="button-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button className="ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="danger" onClick={onConfirm} disabled={!armed || busy}>
            {busy ? 'Deleting…' : 'Delete forever'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatCreatedAt(t: any): string {
  if (!t) return '—';
  // Firestore Timestamp objects come back as { _seconds, _nanoseconds } via REST.
  const ms = typeof t === 'string' ? Date.parse(t) : (t._seconds ? t._seconds * 1000 : (t.seconds ? t.seconds * 1000 : 0));
  if (!ms || isNaN(ms)) return '—';
  return new Date(ms).toLocaleString();
}
